import { useState, useEffect, useRef, useCallback, ReactNode } from 'react';

const INITIAL_COMPONENTS_COUNT = 2;
const FALLBACK_TIMER_MS = 2000;
const OBSERVER_ROOT_MARGIN = '100px';

interface ProgressiveComponentLoaderProps {
  children: ReactNode[];
}

export default function ProgressiveComponentLoader({
  children,
}: ProgressiveComponentLoaderProps) {
  const totalCount = children.length;
  // SSR renders all children so heading structure is visible to crawlers and validators.
  // On the client the count never goes below totalCount (we only grow, never shrink),
  // so there is no hydration mismatch and no layout flash.
  const [loadedCount, setLoadedCount] = useState(totalCount);
  const triggerRef = useRef<HTMLDivElement>(null);
  const allLoaded = loadedCount >= totalCount;

  const loadAll = useCallback(() => {
    setLoadedCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    if (allLoaded) return;

    const timer = setTimeout(loadAll, FALLBACK_TIMER_MS);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadAll();
        }
      },
      { rootMargin: OBSERVER_ROOT_MARGIN }
    );

    if (triggerRef.current) {
      observer.observe(triggerRef.current);
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [allLoaded, loadAll]);

  return (
    <>
      {children.slice(0, loadedCount)}
      {!allLoaded && (
        <div ref={triggerRef} aria-hidden="true" style={{ height: '1px' }} />
      )}
    </>
  );
}
